import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Check,
  Grid3X3,
  MessageSquare,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Plus,
  Search,
  Star,
  StickyNote,
  UserPlus,
  X,
} from "lucide-react";
import { FloatingDialerButton } from "@/components/dialer/FloatingDialerButton";
import { cn } from "@/lib/cn";
import { ROUTES } from "@/constants/routes";
import { useFavouritesStore, useUiStore } from "@/store";
import { usePhoneStore } from "@/features/phone";
import { useMessagesStore } from "@/features/messages/store/useMessagesStore";
import { openOrCreateDmForContact } from "@/features/messages/lib/openOrCreateDm";
import { queryKeys } from "@/constants/queryKeys";
import {
  createDirectoryContact,
  createNote,
  fetchCallLogs,
  fetchDirectoryContacts,
  fetchNotesByNumber,
  type CallLogDirection,
} from "@/services/api";
import { ApiError } from "@/services/http/errors";
import { Button, Field, Input, Modal, toast } from "@/components/ui";
import { mapDirectoryContact } from "@/features/contacts/data/types";
import { filterCallRecords, groupCallLogs } from "../data/groupCallLogs";
import type { CallRecord } from "../data/types";

const DIRECTION_TABS: Array<{ id: CallLogDirection | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "missed", label: "Missed" },
  { id: "incoming", label: "Incoming" },
  { id: "outgoing", label: "Outgoing" },
];

function formatNoteTime(iso?: string) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function CallsWorkspace() {
  const navigate = useNavigate();
  const openDialer = useUiStore((s) => s.openDialer);
  const setDialNumber = usePhoneStore((s) => s.setDialNumber);
  const makeCall = usePhoneStore((s) => s.makeCall);
  const setActiveConversationId = useMessagesStore((s) => s.setActiveConversationId);
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [direction, setDirection] = useState<CallLogDirection | "all">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [chatOpening, setChatOpening] = useState(false);

  const dialNumber = (number: string) => {
    const dest = number.trim();
    if (!dest) return;
    setDialNumber(dest);
    openDialer();
    makeCall(dest);
  };

  const openChatWithCall = async (call: CallRecord) => {
    if (chatOpening) return;
    setChatOpening(true);
    try {
      const conversationId = await openOrCreateDmForContact({
        phone: call.number,
        name: call.hasContact ? call.name : undefined,
      });
      if (!conversationId) {
        toast.error(
          "Can't start chat",
          "No matching teammate found for this number. Chat works with Papaya users in your org.",
        );
        return;
      }
      setActiveConversationId(conversationId);
      void queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
      navigate(ROUTES.messages);
    } catch {
      toast.error("Could not open chat", "Something went wrong. Try again.");
    } finally {
      setChatOpening(false);
    }
  };

  const callLogsQuery = useQuery({
    queryKey: queryKeys.callLogs(direction, query),
    queryFn: () =>
      fetchCallLogs({
        direction: direction === "all" ? undefined : direction,
        search: query.trim() || undefined,
        size: 200,
      }),
    refetchOnWindowFocus: true,
  });

  const contactsQuery = useQuery({
    queryKey: queryKeys.directoryContacts("all", ""),
    queryFn: () => fetchDirectoryContacts(),
    staleTime: 60_000,
  });

  const contacts = useMemo(
    () => (contactsQuery.data ?? []).map(mapDirectoryContact),
    [contactsQuery.data],
  );

  const grouped = useMemo(
    () => groupCallLogs(callLogsQuery.data ?? [], contacts),
    [callLogsQuery.data, contacts],
  );

  const favouriteItems = useFavouritesStore((s) => s.items);
  const isFavourite = useFavouritesStore((s) => s.isFavourite);

  // When API already filtered by direction/search, still apply client grouping filters for safety.
  const filtered = useMemo(
    () =>
      filterCallRecords(grouped, "all", "").map((item) => ({
        ...item,
        favourite: isFavourite(item.number),
      })),
    [grouped, favouriteItems, isFavourite],
  );

  const recent = filtered;
  const selected = filtered.find((item) => item.id === selectedId) ?? null;

  useEffect(() => {
    if (selectedId && !filtered.some((item) => item.id === selectedId)) {
      setSelectedId(filtered[0]?.id ?? null);
    } else if (!selectedId && filtered[0]) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  return (
    <div className="relative flex h-full min-h-0 w-full overflow-hidden rounded-[6px] border border-border bg-surface shadow-[0_12px_36px_rgba(65,33,10,0.08)] dark:shadow-[0_12px_36px_rgba(0,0,0,0.35)]">
      <aside className="flex h-full w-full flex-col lg:w-[300px] lg:max-w-[300px] lg:shrink-0 lg:border-r lg:border-border">
        <div className="flex items-center justify-between gap-2 px-4 pt-4 pb-2">
          <h2 className="text-xl font-semibold text-fg">Calls</h2>
          <div className="flex items-center gap-1">
            <IconBtn label="Dial pad" onClick={openDialer}>
              <Grid3X3 className="h-4 w-4" />
            </IconBtn>
            <IconBtn label="New call" onClick={openDialer}>
              <span className="relative inline-flex">
                <Phone className="h-4 w-4" />
                <Plus className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5" />
              </span>
            </IconBtn>
          </div>
        </div>

        <div className="px-3 pb-2">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-fg-subtle" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name or number"
              className="h-10 w-full rounded-full border border-transparent bg-surface-muted pr-3 pl-9 text-sm text-fg outline-none placeholder:text-fg-subtle focus-visible:ring-2 focus-visible:ring-ring/30"
            />
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto px-3 pb-3">
          {DIRECTION_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setDirection(tab.id)}
              className={cn(
                "shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors",
                direction === tab.id
                  ? "bg-papaya-deep text-white"
                  : "bg-surface-muted text-fg-muted hover:text-fg",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          <p className="mt-1 px-2 py-2 text-xs font-semibold tracking-wide text-fg-subtle uppercase">
            Recent
          </p>
          {callLogsQuery.isLoading ? (
            <p className="px-2 py-3 text-sm text-fg-muted">Loading calls…</p>
          ) : callLogsQuery.isError ? (
            <p className="px-2 py-3 text-sm text-danger">Could not load call history.</p>
          ) : recent.length === 0 ? (
            <p className="px-2 py-3 text-sm text-fg-muted">No calls yet.</p>
          ) : (
            recent.map((item) => (
              <CallRow
                key={item.id}
                call={item}
                active={item.id === selectedId}
                onSelect={() => setSelectedId(item.id)}
              />
            ))
          )}
        </div>
      </aside>

      <section className="relative hidden min-w-0 flex-1 flex-col bg-surface-muted lg:flex">
        {selected ? (
          <CallInfoPanel
            call={selected}
            onClose={() => setSelectedId(null)}
            onDial={() => dialNumber(selected.number)}
            onMessage={() => void openChatWithCall(selected)}
            messaging={chatOpening}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-6 text-sm text-fg-muted">
            Select a call to see details
          </div>
        )}
        <FloatingDialerButton />
      </section>

      <FloatingDialerButton className="lg:hidden" />
    </div>
  );
}

function CallRow({
  call,
  active,
  onSelect,
}: {
  call: CallRecord;
  active: boolean;
  onSelect: () => void;
}) {
  const Icon =
    call.direction === "missed"
      ? PhoneMissed
      : call.direction === "incoming"
        ? PhoneIncoming
        : PhoneOutgoing;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-[6px] px-2 py-2.5 text-left transition-colors",
        active ? "bg-surface-muted" : "hover:bg-surface-muted/70",
      )}
    >
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-papaya/15 text-sm font-semibold text-papaya-deep">
        {call.initials}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-semibold text-fg">{call.name}</span>
          <span className="shrink-0 text-[11px] text-fg-subtle">{call.time}</span>
        </span>
        <span
          className={cn(
            "mt-0.5 flex items-center gap-1.5 text-xs",
            call.direction === "missed" ? "text-danger" : "text-fg-muted",
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          <span>
            {call.direction === "missed"
              ? "Missed"
              : call.direction === "incoming"
                ? "Incoming"
                : "Outgoing"}
          </span>
          {call.hasContact ? (
            <span className="text-fg-subtle">· Contact</span>
          ) : null}
        </span>
      </span>
    </button>
  );
}

function CallInfoPanel({
  call,
  onClose,
  onDial,
  onMessage,
  messaging,
}: {
  call: CallRecord;
  onClose: () => void;
  onDial: () => void;
  onMessage: () => void;
  messaging?: boolean;
}) {
  const queryClient = useQueryClient();
  const toggleFavourite = useFavouritesStore((s) => s.toggleFavourite);
  const starred = useFavouritesStore((s) =>
    s.items.some((item) => item.key === (call.number.replace(/\D/g, "") || call.number.trim())),
  );
  const numberKey = call.number.replace(/\D/g, "") || call.number;
  const [draft, setDraft] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState(call.number);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setDraft("");
    setAddOpen(false);
    setContactName(call.hasContact ? call.name : "");
    setContactPhone(call.number);
    setFormError(null);
  }, [call.id, call.number, call.name, call.hasContact]);

  const notesQuery = useQuery({
    queryKey: queryKeys.notesByNumber(numberKey),
    queryFn: () => fetchNotesByNumber(call.number),
    enabled: Boolean(call.number),
  });

  const saveMutation = useMutation({
    mutationFn: () => createNote({ content: draft.trim(), number: call.number }),
    onSuccess: async () => {
      setDraft("");
      toast.success("Note saved");
      await queryClient.invalidateQueries({ queryKey: queryKeys.notesByNumber(numberKey) });
    },
    onError: () => {
      toast.error("Could not save note");
    },
  });

  const createContactMutation = useMutation({
    mutationFn: createDirectoryContact,
    onSuccess: async (created) => {
      setAddOpen(false);
      setFormError(null);
      setContactName("");
      toast.success("Contact saved", created.name);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["directory-contacts"] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.callLogsRoot }),
      ]);
    },
    onError: (err) => {
      const message =
        err instanceof ApiError ? err.userMessage : "Could not create contact.";
      setFormError(message);
      toast.error("Could not create contact", message);
    },
  });

  const openAddContact = () => {
    setContactName("");
    setContactPhone(call.number);
    setFormError(null);
    setAddOpen(true);
  };

  const saveContact = () => {
    if (!contactName.trim()) {
      setFormError("Name is required.");
      return;
    }
    setFormError(null);
    createContactMutation.mutate({
      name: contactName.trim(),
      phone: contactPhone.trim() || call.number,
    });
  };

  const notes = notesQuery.data ?? [];

  const timeline = useMemo(() => {
    type TimelineItem =
      | { kind: "call"; event: (typeof call.history)[number] }
      | { kind: "note"; note: (typeof notes)[number] };

    const items: TimelineItem[] = [];
    const usedNoteIds = new Set<number>();

    call.history.forEach((event, index) => {
      items.push({ kind: "call", event });
      const lower = Date.parse(event.startedAt) || 0;
      const newer = call.history[index - 1];
      const upper = newer ? Date.parse(newer.startedAt) || Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY;

      const related = notes
        .filter((note) => {
          if (usedNoteIds.has(note.id)) return false;
          const t = Date.parse(note.created_at || "") || 0;
          return t > lower && t <= upper;
        })
        .sort(
          (a, b) =>
            (Date.parse(b.created_at || "") || 0) - (Date.parse(a.created_at || "") || 0),
        );

      for (const note of related) {
        usedNoteIds.add(note.id);
        items.push({ kind: "note", note });
      }
    });

    // Notes older than every call (or notes when there is no history)
    const remaining = notes
      .filter((note) => !usedNoteIds.has(note.id))
      .sort(
        (a, b) =>
          (Date.parse(b.created_at || "") || 0) - (Date.parse(a.created_at || "") || 0),
      );
    for (const note of remaining) {
      items.push({ kind: "note", note });
    }

    return items;
  }, [call.history, notes]);

  return (
    <>
      <header className="flex items-center justify-between border-b border-border bg-surface px-4 py-3">
        <h3 className="text-base font-semibold text-fg">Call info</h3>
        <IconBtn label="Close" onClick={onClose}>
          <X className="h-4 w-4" />
        </IconBtn>
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="overflow-hidden rounded-[8px] border border-border bg-surface">
          <div className="flex items-center gap-3 border-b border-border px-4 py-4">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-papaya/15 text-lg font-semibold text-papaya-deep">
              {call.initials}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-semibold text-fg">{call.name}</p>
              <p className="truncate text-sm text-fg-muted">{call.number}</p>
              {call.hasContact ? (
                <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-papaya-leaf">
                  <Check className="h-3 w-3" />
                  Saved contact
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-1 text-fg-muted">
              <IconBtn
                label={starred ? "Remove from favourites" : "Add to favourites"}
                onClick={() =>
                  toggleFavourite({
                    name: call.name,
                    number: call.number,
                  })
                }
              >
                <Star
                  className={cn(
                    "h-4 w-4",
                    starred && "fill-papaya text-papaya",
                  )}
                />
              </IconBtn>
              <IconBtn
                label={messaging ? "Opening chat…" : "Message"}
                onClick={onMessage}
                disabled={messaging}
              >
                <MessageSquare className="h-4 w-4" />
              </IconBtn>
              <IconBtn label="Call" onClick={onDial}>
                <Phone className="h-4 w-4" />
              </IconBtn>
            </div>
          </div>

          {!call.hasContact ? (
            <div className="border-b border-border px-4 py-3">
              <button
                type="button"
                onClick={openAddContact}
                className="inline-flex w-full items-center justify-center gap-2 rounded-[6px] border border-dashed border-border bg-surface-muted px-3 py-2.5 text-sm font-medium text-fg transition-colors hover:border-papaya/40 hover:bg-papaya/5"
              >
                <UserPlus className="h-4 w-4 text-papaya-deep" />
                Add to contacts
              </button>
            </div>
          ) : null}

          <div className="px-4 py-3">
            <p className="mb-3 text-xs font-semibold tracking-wide text-fg-subtle uppercase">
              Call history
            </p>

            {notesQuery.isLoading && call.history.length === 0 ? (
              <p className="text-sm text-fg-muted">Loading…</p>
            ) : timeline.length === 0 ? (
              <p className="text-sm text-fg-muted">No history for this number.</p>
            ) : (
              <ul className="space-y-2.5">
                {timeline.map((item) => {
                  if (item.kind === "call") {
                    const { event } = item;
                    const Icon =
                      event.direction === "missed"
                        ? PhoneMissed
                        : event.direction === "incoming"
                          ? PhoneIncoming
                          : PhoneOutgoing;
                    return (
                      <li
                        key={`call-${event.id}`}
                        className="flex items-start gap-3 rounded-[6px] bg-surface-muted/60 px-2.5 py-2"
                      >
                        <span
                          className={cn(
                            "mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full",
                            event.direction === "missed"
                              ? "bg-danger/10 text-danger"
                              : "bg-papaya-leaf/10 text-papaya-leaf",
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-fg">{event.label}</p>
                          <p className="text-xs text-fg-muted">{event.time}</p>
                        </div>
                        <span className="shrink-0 text-xs text-fg-subtle">{event.status}</span>
                      </li>
                    );
                  }

                  const { note } = item;
                  return (
                    <li
                      key={`note-${note.id}`}
                      className="relative ml-1 overflow-hidden rounded-[10px] border border-amber-500/25 bg-gradient-to-br from-amber-50 to-orange-50/90 px-3.5 py-3 shadow-[0_6px_16px_rgba(180,83,9,0.07)] dark:border-amber-400/20 dark:from-amber-950/45 dark:to-orange-950/30"
                    >
                      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-amber-400 to-orange-500" />
                      <p className="mb-1.5 flex items-center gap-1.5 pl-1.5 text-[11px] font-semibold tracking-wide text-amber-800 uppercase dark:text-amber-200">
                        <StickyNote className="h-3.5 w-3.5" />
                        Note
                      </p>
                      <p className="pl-1.5 whitespace-pre-wrap text-sm leading-relaxed text-fg">
                        {note.content}
                      </p>
                      {formatNoteTime(note.created_at) ? (
                        <p className="mt-2 pl-1.5 text-[11px] text-amber-900/55 dark:text-amber-100/50">
                          {formatNoteTime(note.created_at)}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="mt-4 border-t border-border pt-3">
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Add a note…"
                rows={3}
                className="w-full resize-none rounded-[6px] border border-border bg-surface-muted px-3 py-2 text-sm text-fg outline-none placeholder:text-fg-subtle focus-visible:ring-2 focus-visible:ring-ring/30"
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  disabled={!draft.trim() || saveMutation.isPending}
                  onClick={() => saveMutation.mutate()}
                  className="rounded-full bg-papaya-deep px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {saveMutation.isPending ? "Saving…" : "Save note"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={addOpen}
        onClose={() => {
          if (createContactMutation.isPending) return;
          setAddOpen(false);
          setFormError(null);
        }}
        title="Add to contacts"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setAddOpen(false)}
              disabled={createContactMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={saveContact}
              loading={createContactMutation.isPending}
              disabled={!contactName.trim()}
            >
              Save contact
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field
            label="Name"
            htmlFor="call-contact-name"
            error={formError && !contactName.trim() ? formError : undefined}
          >
            <Input
              id="call-contact-name"
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
              placeholder="Full name"
              autoFocus
            />
          </Field>
          <Field label="Phone" htmlFor="call-contact-phone">
            <Input
              id="call-contact-phone"
              value={contactPhone}
              onChange={(event) => setContactPhone(event.target.value)}
              placeholder="+977…"
            />
          </Field>
          {formError && contactName.trim() ? (
            <p className="text-xs text-danger">{formError}</p>
          ) : null}
        </div>
      </Modal>
    </>
  );
}

function IconBtn({
  label,
  children,
  onClick,
  disabled,
}: {
  label: string;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-fg-muted hover:bg-surface-muted hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
